import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import NotesPanel from "@/components/NotesPanel";
import ClinicalBrainPanel from "@/components/ClinicalBrainPanel";
import PortalPanel from "@/components/PortalPanel";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Stethoscope, ScanLine, User, FileText, Brain, Activity, Mail, Loader2 } from "lucide-react";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [moti, setMoti] = useState(null);
  const [nudging, setNudging] = useState(false);

  useEffect(() => {
    api.get(`/patients/${id}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/patients/${id}/moti`).then((r) => setMoti(r.data)).catch(() => {});
  }, [id]);

  const p = data?.patient;

  const nudgePatient = async () => {
    if (!p?.email) { toast.error("No email on file for this patient"); return; }
    if (!window.confirm(`Send a booking reminder email to ${p.name} (${p.email})?`)) return;
    setNudging(true);
    try {
      const { data: res } = await api.post(`/retention/${p.cliniko_id}/nudge`, {});
      toast.success(`Reminder emailed to ${res.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
    finally { setNudging(false); }
  };

  return (
    <Layout title={p?.name || "Patient"} subtitle={p?.diagnosis}
      actions={<Button variant="outline" onClick={() => navigate("/patients")}><ArrowLeft className="mr-1.5 h-4 w-4" /> Patients</Button>}>
      {p && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-4 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-teal-600 text-white"><User className="h-6 w-6" /></div>
                <div><div className="font-heading font-bold text-slate-900">{p.name}</div><div className="font-mono text-xs text-slate-400">{p.cliniko_id}</div></div>
              </div>
              <dl className="mt-4 space-y-2 text-sm">
                <Row label="Clinician" value={p.clinician} />
                <Row label="DOB" value={p.dob} />
                <Row label="Last visit" value={p.last_visit ? new Date(p.last_visit).toLocaleDateString("en-GB") : "—"} />
                <Row label="Active plan" value={p.has_active_plan ? "Yes" : "No"} />
              </dl>
              <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600"><b className="text-slate-400">Intake:</b> {p.intake}</div>
              <Button onClick={() => navigate(`/builder?patient=${p.cliniko_id}`)} data-testid="build-plan-for-patient-btn" className="mt-4 w-full bg-teal-600 hover:bg-teal-700">
                <Stethoscope className="mr-1.5 h-4 w-4" /> Build plan
              </Button>
              <Button onClick={nudgePatient} disabled={nudging || !p.email} variant="outline" data-testid="patient-nudge-btn" className="mt-2 w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50" title={p.email ? `Email ${p.email}` : "No email on file"}>
                {nudging ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Email booking reminder
              </Button>
            </div>

            {moti?.available && (
              <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5">
                <div className="flex items-center gap-2 text-sm font-bold text-indigo-700"><ScanLine className="h-4 w-4" /> Moti Physio scan <span className="rounded bg-indigo-100 px-1.5 text-[10px] font-semibold text-indigo-600">MOCK</span></div>
                <p className="mt-2 text-sm text-slate-600">{moti.scan.summary}</p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {Object.entries(moti.scan.metrics || {}).map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-white p-2 text-center ring-1 ring-indigo-100">
                      <div className="font-heading text-lg font-bold tabular-nums text-indigo-700">{v}</div>
                      <div className="text-[9px] uppercase tracking-wide text-slate-400">{k.replace(/_/g, " ")}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-8">
            <Tabs defaultValue="notes">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="notes" data-testid="patient-tab-notes"><FileText className="mr-1.5 h-4 w-4" /> Clinical Notes</TabsTrigger>
                <TabsTrigger value="brain" data-testid="patient-tab-brain"><Brain className="mr-1.5 h-4 w-4" /> Clinical Brain</TabsTrigger>
                <TabsTrigger value="portal" data-testid="patient-tab-portal"><Activity className="mr-1.5 h-4 w-4" /> Portal &amp; check-ins</TabsTrigger>
              </TabsList>
              <TabsContent value="notes" className="mt-4"><NotesPanel clinikoId={id} /></TabsContent>
              <TabsContent value="brain" className="mt-4"><ClinicalBrainPanel clinikoId={id} /></TabsContent>
              <TabsContent value="portal" className="mt-4"><PortalPanel clinikoId={id} /></TabsContent>
            </Tabs>
          </div>
        </div>
      )}
    </Layout>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between"><dt className="text-slate-400">{label}</dt><dd className="font-medium text-slate-700">{value}</dd></div>
);
